import { useCallback, useEffect, useMemo, useState, useRef, MouseEvent } from 'react'
import moment from 'moment-timezone'
import * as yup from 'yup'
import makeStyles from '@mui/styles/makeStyles'

import ClientCampaignsResource from '../../resources/ClientCampaignsResource'
import AllocationsResource from '../../resources/AllocationsResource'
import ContractSelector, { ALL_CONTRACT, isAllContract } from '../ContractSelector'
import { Form, getIn, serializeNestedRecord, useFormikContext } from '../Formik/forms'

import TextField from '@mui/material/TextField'
import { InputProps } from '@mui/material/Input'
import InputAdornment from '@mui/material/InputAdornment'
import Tooltip from '@mui/material/Tooltip'
import Box from '@mui/material/Box'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'

import AllocationsTableFooter from './AllocationsTableFooter'
import MonthSelector from '../UI/MonthSelector'
import DailyCapModal from '../DailyCapModal/DailyCapModal'
import TableContainer from '../SimpleTable/TableContainer'
import TableContent from '../SimpleTable/TableContent'
import TableTitle from '../SimpleTable/TableTitle'
import AddCircleIcon from '@mui/icons-material/AddCircle';

import DateRangeIcon from '@mui/icons-material/DateRange'

import { downloadData, failOnHttpError, isBlank, isPresent, multiplyOrNull, readFile, sum, toDollars, toFormattedNumber } from '../../lib/utils'
import { ClientCampaignRecord, ContractRecord, getRecordIdentity, newRecord } from '../../lib/DataModel'
import { camelize } from '@orbit/serializers'
import { InitializedRecord } from '@orbit/records'
import { useDataManager } from 'components/SimpleTable/DataManager'
import { useNotifications } from 'lib/NotificationsProvider'
import { useFeatures } from 'providers/FeaturesProvider'
import { isEqual, pickBy, sumBy, uniq } from 'lodash-es';
import ContractsResource from 'resources/ContractsResource'
import { useCurrentUser } from 'lib/CurrentUserProvider'
import { useParams } from 'react-router-dom'
import { useOrbit } from 'providers/OrbitProvider'
import Checkbox from '@mui/material/Checkbox'
import Stack from '@mui/material/Stack'
import DownloadImage from '@mui/icons-material/FileDownloadOutlined';
import UploadIcon from '@mui/icons-material/FileUploadOutlined';
import StandardDialog from 'components/UI/Dialog/StandardDialog'
import Typography from '@mui/material/Typography'
import Papa from 'papaparse'
import notifyError from 'shared/error_reporter'
import DetailsImage from 'details-icon.png'
import AddClientCampaignModal from 'components/AddClientCampaignModal'
import SendIOPreviewContainer, { RecipientData } from './SendIOPreview'
import FilePickerWithLabel from 'components/UI/FilePickerWithLabel/FilePickerWithLabel'
import CampaignSpend from './CampaignSpend'


export const PAYOUT_LABEL_MAP = {
  cost_per_lead: 'Fixed CPL',
  variable_cost_per_lead: 'Variable CPL',
  cost_per_enroll: 'CPE',
}

const useStyles = makeStyles(theme => ({
  'smartScore-green': {
    color: theme.palette.success.main,
  },
  'smartScore-yellow': {
    color: theme.palette.warning.main,
  },
  'smartScore-red': {
    color: theme.palette.error.main,
  },
}))

function BlankSmartScoreIcon() {
  return (
    <Box
      sx={{
        fontSize: 14,
        fontWeight: 700
      }}
    >
      <span>
        —
      </span>
    </Box>
  )
}

function SmartScore({smartScore, smartScoreExplanation}) {
  const classes = useStyles()

  const iconLookup = {
    green: TrendingUpIcon,
    yellow: TrendingFlatIcon,
    red: TrendingDownIcon
  }

  const Icon = iconLookup[smartScore] || BlankSmartScoreIcon

  if(smartScoreExplanation) {
    return (
      <Tooltip title={smartScoreExplanation}>
        <Icon className={`${classes[`smartScore-${smartScore}`]}`}/>
      </Tooltip>
    )
  } else {
    return (
      <Icon className={`${classes[`smartScore-${smartScore}`]}`}/>
    )
  }
}

type Payout = 'cost_per_lead' | 'cost_per_enroll' | 'variable_cost_per_lead'
type SmartScore = 'green' | 'yellow' | 'red'

type AllocationRecord = InitializedRecord & {
  attributes: {
    year: number
    month: number
    cplInCents: number
    originalCap: number
    adjustedCap: number
    vendorProjection: number
    budgetInCents: number
  }
}

export interface AllocationRow {
  id: string
  clientCampaign: InitializedRecord
  clientCampaignName: string
  clientCampaignId: string
  clientCampaignPublicId: string
  clientCampaignProgramGroup: string
  payout: Payout
  smartScore: SmartScore
  smartScoreExplanation: string
  acceptedLeadCount: number
  totalCostInCents: number
  cplInCents: number
  vendorProjection: number
  budgetInCents: number
  minimumLeadTarget: number
  originalCap: number
  adjustedCap: number
  allocationId: string
  allocation: AllocationRecord
  originalAttributes: AllocationRecord['attributes']
  vendorName: string
}

interface AllocationFieldProps {
  row: AllocationRow
  field: string
  format?: "number" | "currency"
  onChange?: (row: AllocationRow, field: string, value: string | number | null) => void
  placeholder?: string
  disabled?: boolean
}

type FormValues = Record<string, unknown>

function isBlankAllocation(row: AllocationRow) {
  if(row.payout === 'variable_cost_per_lead') {
    return isBlank(row.minimumLeadTarget) && isBlank(row.budgetInCents)
  }
  else {
    return isBlank(row.cplInCents) && isBlank(row.originalCap)
  }
}

export function getFieldStyling(isCurrency: boolean, width = 96) {
  return {
    width: width,
    mr: '-12px',
    '.MuiOutlinedInput-root': {
      backgroundColor: '#FFF',
      paddingLeft: 0
    },
    '.MuiOutlinedInput-input': {
      textAlign: 'right',
      lineHeight: '20px',
      pl: isCurrency ? 0 : undefined
    },
    '.Mui-error': {
      '.MuiOutlinedInput-notchedOutline': {
        borderWidth: 2,
        borderColor: '#cd7865 !important'
      }
    }
  }
}

export function CurrencyAdornment() {
  return (
    <InputAdornment
      position="start"
      disableTypography={true}
      sx={{
        marginRight: '4px',
        marginLeft: '12px',
        fontSize: 14,
        color: 'text.secondary',
      }}
    >
      $
    </InputAdornment>
  )
}

function CurrentSpendField({value, placeholder, onClick, disabled}) {
  return (
    <TextField
      InputProps={{ startAdornment: <CurrencyAdornment /> }}
      sx={getFieldStyling(true, 108)}
      size="small"
      value={value}
      placeholder={placeholder}
      onClick={() => onClick()}
      disabled={disabled}
    />
  )
}

function AllocationField({row, field, format, onChange, placeholder, disabled} : AllocationFieldProps) {
  const inputRef = useRef<HTMLInputElement>()
  const dataManager = useDataManager<AllocationRow>()

  const { errors } = useFormikContext()
  const hasError = !!getIn(errors, `${row.id}.${field}`)

  // Determine if this field is the first error for autoFocus
  const isFirstError = useMemo(() => {
    const firstCampaignId = Object.keys(errors)[0]
    if(row.id !== firstCampaignId)
      return false
    return Object.keys(errors[firstCampaignId])[0] === field
  }, [errors, row.id, field])

  useEffect(() => {
    if(isFirstError && inputRef.current)
      inputRef.current.focus()
  }, [isFirstError])

  const toFormatted = useCallback((raw: string) => {
    if(format === 'currency') {
      return toDollars(raw, { useGrouping: false, minimumFractionDigits: 2, maximumFractionDigits: 2 })
    } else if(format === 'number') {
      return toFormattedNumber(raw)
    } else {
      return raw
    }
  }, [format])

  const currentValue = toFormatted(getIn(row.allocation, `attributes.${field}`))
  const [displayValue, setDisplayValue] = useState<string|null>(null)

  const fromFormatted = useCallback((formatted: string) => {
    if(format === 'currency') {
      if(formatted === '') {
        return null
      } else {
        return Math.round(parseFloat(formatted.replace(/[^0-9.]/g, '')) * 100);
      }
    } else if(format === 'number') {
      if(formatted === '') {
        return null
      } else {
        return parseInt(formatted.replace(/[^0-9.]/g, ''));
      }
    } else {
      return formatted
    }
  }, [format])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if(hasError) {
      delete errors[row.id]
    }
    const newValue = fromFormatted(event.target.value)
    setDisplayValue(event.target.value)
    onChange?.(row, field, newValue)
  }, [fromFormatted, setDisplayValue, onChange, row, field, hasError, errors])

  const finalizeDisplayValue = useCallback((_event: unknown) => {
    setDisplayValue(null)
  }, [setDisplayValue])

  const interceptEnter = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if(event.key === 'Enter') {
      event.currentTarget.blur()
    }
  }, [])

  const isCurrency = format === 'currency'

  const inputProps : Partial<InputProps> = useMemo(() => {
    if(isCurrency) {
      return {
        startAdornment: <CurrencyAdornment />,
      }
    } else if (format === 'number') {
      return {
        inputMode: 'numeric',
        pattern: '[0-9]*'
      }
    } else {
      return {}
    }
  }, [format, isCurrency])

  return (
    <TextField
      InputProps={inputProps}
      sx={getFieldStyling(isCurrency, field === 'budgetInCents' ? 108 : undefined)}
      size="small"
      value={displayValue ?? currentValue}
      onChange={handleChange}
      onBlur={finalizeDisplayValue}
      onKeyDown={interceptEnter}
      placeholder={placeholder}
      error={hasError}
      inputRef={inputRef}
      disabled={disabled || dataManager.loading}
    />
  )
}

function AdjustedCapFields({row, onChange, disabled }) {
  const [checked, setChecked] = useState(!!row.adjustedCap)

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if(!event.currentTarget.checked) {
      const hasAdjustedCap = row.adjustedCap !== null
      if(hasAdjustedCap) {
        onChange(row, 'adjustedCap', null)
      }
    }

    setChecked(event.target.checked)
  }, [onChange, row])

  useEffect(() => {
    setChecked(isPresent(row.adjustedCap))
  }, [row.adjustedCap])

  return(
    <>
    {isPresent(row.originalCap) &&
      <Stack direction='row-reverse' sx={{ alignItems: 'center'}} >
        <Checkbox
          disabled={disabled}
          aria-label='Enable Adjusted Cap'
          size='medium'
          checked={checked}
          onChange={handleChange}
          sx={{ m: 0, mr: '-6px', ml: '15px', p: '4px', height: '32 px' }}
        />
        {checked &&
          <AllocationField row={row} field='adjustedCap' format='number' onChange={onChange} placeholder='0' />
        }
      </Stack>
    ||
      <Box sx={{ fontWeight: 700 }}>
        —
      </Box>
    }
    </>
  )
}

function AllocationsTableContent({ reloadContent, setNestedValue, deleteNestedValue, importOpen, setImportOpen, columns, permitDeleteAllocation, permitEditCap, permitEditCPL, permitEditBudget, permitEditLeadTarget, permitEditVendorProjection, permitEditCampaigns, onEditCampaign }) {
  const { values, errors, resetForm } = useFormikContext<FormValues>()

  const setValue = useCallback((row: AllocationRow, field: string, value: string|number|null) => {
    row.allocation.attributes[field] = value
    row[field] = value
    if (isEqual(pickBy(row.allocation.attributes, value => isPresent(value)), pickBy(row.originalAttributes, value => isPresent(value)))) {
      if (values['_nested']) {
        // Remove the entire field from the form values and reset the form to undirty it if necessary
        const campaignIndex = values['_nested']['client_campaigns_attributes'].findIndex((r: AllocationRow) => r.id === row.clientCampaignId)
        values['_nested']['client_campaigns_attributes'].splice(campaignIndex, 1)
        if(values['_nested']['client_campaigns_attributes'].length === 0) {
          resetForm()
        }
      }
    } else if(permitDeleteAllocation && isBlankAllocation(row)) {
      deleteNestedValue('clientCampaigns.allocations', row.allocation)
    } else {
      setNestedValue('clientCampaigns.allocations', row.allocation)
    }
  }, [permitDeleteAllocation, values, resetForm, deleteNestedValue, setNestedValue])

  const handleChange = useCallback((row: AllocationRow, field: string, value: string|number|null) => {
    setValue(row, field, value)
    reloadContent()
  }, [reloadContent, setValue])

  return (
    <>
      <TableContent
        title={"Campaign Allocations"}
        options={{
          topSummaryRow: true,
          hover: true,
          renderContext: {handleChange},
          rowErrors: errors,
          subgroupColumn: 'vendorName',
          totalColumn:'clientCampaignName',
        }}
        rowActions={ [
          permitEditCampaigns && {
            icon: DetailsImage,
            tooltip: 'Edit Campaign',
            onClick: (_event: MouseEvent, rowData: Record<string,string>) => { onEditCampaign(rowData.id) },
          }
        ].filter(Boolean)}
      />
      {importOpen && (
        <ImportAllocationDialog onClose={() => { setImportOpen(false) }} columns={columns} permitEditCap={permitEditCap} permitEditCPL={permitEditCPL} permitEditBudget={permitEditBudget} permitEditLeadTarget={permitEditLeadTarget} permitEditVendorProjection={permitEditVendorProjection} reloadContent={reloadContent} />
      )}
    </>
  )
}

export const generateAllocationSchema = (cplLabel: string) => yup.object().shape({
  original_cap: yup.number().min(0).typeError('Cap must be a number'),
  adjusted_cap: yup.number()
    .optional()
    .nullable()
    .min(0, 'Adjusted Cap must be at least 0')
    .typeError('Adjusted Cap must be a number'),
  cpl_in_cents: yup.number().nullable()
    .when('original_cap', {
      is: (val: number) => isPresent(val),
      then: yup.number().required(`${cplLabel} is required`).min(0, `${cplLabel} is required`).typeError(`${cplLabel} is required`)
    })
    .when('adjusted_cap', {
      is: (val: number) => isPresent(val),
      then: yup.number().required(`${cplLabel} is required`).min(0, `${cplLabel} is required`).typeError(`${cplLabel} is required`)
    }),
  minimum_lead_target: yup
    .number()
    .nullable()
    .min(0, 'Minimum Lead Target must be at least 0')
    .typeError('Minimum Lead Target must be a number'),

  budget_in_cents: yup
    .number()
    .nullable()
    .min(0, 'Budget must be at least 0')
    .typeError('Budget must be a number'),
  })
  .test(
    'budget-minimumLeadTarget-pairing',
    'Both Minimum Lead Target and Budget must be set for variable CPL allocations',
    function (value) {
      const { minimum_lead_target, budget_in_cents } = value;
      const hasMinimumLeadTarget = minimum_lead_target != null;
      const hasBudget = budget_in_cents != null;

      return (
        (!hasMinimumLeadTarget && !hasBudget) ||
        (hasMinimumLeadTarget && hasBudget)
      );
    }
)


function AllocationsTableForm({ contract, month, data, setData, refreshData, setRefreshData, cplLabel, client, permitSendIO, permitSave, importOpen, setImportOpen, columns, permitDeleteAllocation, permitEditCap, permitEditCPL, permitEditBudget, permitEditLeadTarget, permitEditVendorProjection, permitEditCampaigns, onEditCampaign }) {
  const { addNotification, clearNotifications } = useNotifications()
  const { getRemoteId } = useOrbit()
  const allocationSchema = generateAllocationSchema(cplLabel)

  const validate = (values: FormValues) => {
    const campaignAttrs = values['_nested']?.['client_campaigns_attributes'] || []
    const errors: Record<string, Record<string, string[]>> = {}
    campaignAttrs.forEach((campaign) => {
      const allocation = campaign['allocations_attributes'][0] || {}

      try {
        allocationSchema.validateSync(allocation)
      } catch(e) {
        if (e instanceof yup.ValidationError) {
          errors[campaign.id] = { [camelize(e.path || '')]: e.errors } // TODO: in what case is path undefined?
        } else {
          throw e
        }
      }
    })

    // Flatten out all error messages stored in nested errors object to only show unique messages
    // e.g { campaignId => { fieldName => [error_message] } }
    const errorMessages = Object.values(errors).map((campaignErrors) => Object.values(campaignErrors)).flat(2)
    const uniqueErrors = [...new Set(errorMessages)]
    uniqueErrors.forEach((error) => {
      addNotification({variant: 'alert', message: error as string})
    })

    return errors
  }

  // Post to client endpoint in non-vendor case and do not reset contract
  const resource = (!contract || contract.id === ALL_CONTRACT.id) ? client : contract

  const [openSendIOPreview, setOpenSendIOPreview] = useState(false)

  const handleSendOrder = useCallback((values : RecipientData, { setSubmitting })=> {
    setSubmitting(true)
    clearNotifications()

    const url = `/api/v1/contracts/${getRemoteId(contract)}/insertion-orders`
    const requestOptions = {
      method: "POST",
      body: JSON.stringify({ io: {
        month: month.month()+1,
        year: month.year(),
        to: values.toEmails,
        cc: values.ccEmails,
      }}),
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      }
    }

    fetch(url, requestOptions)
      .then(failOnHttpError)
      .then(_result => {
        addNotification({variant: 'notice', message: "Successfully sent IO."})
        setOpenSendIOPreview(false)
      })
      .catch(_error => {
        addNotification({variant: 'alert', message: "Error sending IO."});
      })
      .finally(() => {
        setSubmitting(false)
      })
  }, [addNotification, clearNotifications, contract, getRemoteId, month])

  return (
    <>
      <Form
        resource={resource}
        redirectAfterSave={false}
        resetOnChange={[contract, month]}
        promptOnNavigate={true}
        validate={validate}
        withoutSubmitButton
      >
        {({setNestedValue, deleteNestedValue, resetForm}) => (
          <>
            <AllocationsTableContent
              setNestedValue={setNestedValue}
              deleteNestedValue={deleteNestedValue}
              reloadContent={() => setData([...data])}
              importOpen={importOpen}
              setImportOpen={setImportOpen}
              columns={columns}
              permitDeleteAllocation={permitDeleteAllocation}
              permitEditCap={permitEditCap}
              permitEditCPL={permitEditCPL}
              permitEditBudget={permitEditBudget}
              permitEditLeadTarget={permitEditLeadTarget}
              permitEditVendorProjection={permitEditVendorProjection}
              permitEditCampaigns={permitEditCampaigns}
              onEditCampaign={onEditCampaign}
            />
            <AllocationsTableFooter
              permitSendIO={permitSendIO}
              permitSave={permitSave}
              contract={contract}
              month={month}
              refreshData={() => setRefreshData(!refreshData)}
              onCancel={() => {
                setRefreshData(!refreshData)
                resetForm()
              }}
              setOpenSendIOPreview={setOpenSendIOPreview}
            />
          </>
        )}
      </Form>

      {openSendIOPreview && data &&
        <SendIOPreviewContainer onClose={() => setOpenSendIOPreview(false)} contract={contract} data={data} onComplete={handleSendOrder} month={month} />
      }
    </>
  )
}

function getPayoutAbbrev(payout: string) {
  if(payout === 'cost_per_enroll') {
    return 'CPE'
  }

  return 'CPL'
}

// Payout column title is:
//    "CPE" or "CPL" if all rows are of the same payout type
//    "CPE/CPL" if the rows have a mix of CPE & CPL campaigns
//    "CPL" if there are no rows
function getPayoutColumnTitle(data: AllocationRow[] | null) {
  if(data === null || isBlank(data)) {
    return "CPL"
  }

  return uniq(data.map(row => getPayoutAbbrev(row.payout)))
    .sort()
    .join('/')
}

type CsvColumnName =
  "Campaign" |
  "Campaign ID" |
  "Type" |
  "Program Group" |
  "CPL" |
  "Cap" |
  "Adjusted Cap" |
  "Accepted Leads" |
  "Spend" |
  "Budget" |
  "Vendor Projection"

type CsvRow = Record<CsvColumnName,string>

function ImportAllocationDialog({onClose, columns, permitEditCap, permitEditCPL, permitEditLeadTarget, permitEditBudget, permitEditVendorProjection, reloadContent}) {
  const dataManager = useDataManager()
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [file, setFile] = useState<File>()
  const { setValues } = useFormikContext()
  const { store } = useOrbit()
  const { addNotification } = useNotifications()

  const handleFile = () => {
    setError(null);
    setProcessing(true);


    if (file) {
      readFile(file)
      .then(fileContents => {
        const csv = Papa.parse<CsvRow>(fileContents as string, { header: true });

        // Use field name for any explicit references and map to title as needed (field is less likely to be changed)
        const permittedFields = [
          "clientCampaignPublicId",
          permitEditCPL && "cplInCents",
          permitEditCap && "originalCap",
          permitEditCap && "adjustedCap",
          permitEditLeadTarget && "minimumLeadTarget",
          permitEditBudget && "budgetInCents",
          permitEditVendorProjection && "vendorProjection"
        ].filter(Boolean);

        // Create bidirectional mappings
        const fieldToTitle = {}
        const titleToField = {}
        columns.forEach(({ field, title }) => {
          fieldToTitle[field] = title
          titleToField[title] = field
        })

        const desiredColumns = permittedFields.map(field => fieldToTitle[field])
        const filteredData = csv.data.map(row =>
          Object.fromEntries(
            Object.entries(row).filter(([key]) => desiredColumns.includes(key))
          )
        )

        const transformedData = filteredData.reduce((result, item) => {
          const campaignID = item[fieldToTitle["clientCampaignPublicId"]]
          if (!campaignID) return result; // Skip if Campaign ID is missing

          const transformedItem = {};

          for (const [key, value] of Object.entries(item)) {
            if (key === fieldToTitle["clientCampaignPublicId"]) continue; // Skip, Campaign ID will be used as a key

            if (typeof value === "string" && value.trim() === "") {
              // Convert empty strings or strings with only spaces to null
              transformedItem[key] = null
            } else if (key === fieldToTitle["cplInCents"] || key === fieldToTitle["budgetInCents"]) {
              // Remove the "$", convert to a number, and multiply by 100
              transformedItem[key] = value ? Number(value.replace(/[$,]/g, '')) * 100 : null
            } else {
              // Convert other strings to numbers if applicable
              transformedItem[key] = value && !isNaN(Number(value.trim())) ? Number(value.trim()) : value
            }
          }

          result[campaignID] = transformedItem;
          return result;
        }, {} as Record<string,Partial<CsvRow>>);

        dataManager.data.forEach(row => {
          const updatedProperties = transformedData[row.clientCampaignPublicId]
          const titleValuePairs = updatedProperties ? Object.entries(updatedProperties) : []
          titleValuePairs.forEach(([title, value]) => {
            const field = titleToField[title]

            // Check if field is editable and not disabled
            const canEdit =
              row.fieldMetadata?.[field].editable &&
              !row.fieldMetadata?.[field].disabled

            if (canEdit) {
              row.allocation.attributes[field] = value
              row[field] = value
            }
          });
        })

        setValues(values => {
          const newValues = { ...values }
          newValues['_nested'] ||= {}
          newValues['_nested']['client_campaigns_attributes'] = dataManager.data.map(row => {
            // Delete existing record if it exists and
            // is not vCPL and both cplInCents and originalCap are nullified
            // is vCPL and both minumumLeadTarget and budgetInCents are nullified
            if(row.allocationId &&
               (row.payout !== "variable_cost_per_lead" && row.cplInCents === null && row.originalCap === null) ||
               (row.payout === "variable_cost_per_lead" && row.minimumLeadTarget === null && row.budgetInCents === null)) {
              return {
                __id: row.clientCampaign.id,
                __record: row.clientCampaign,
                id: row.clientCampaignId,
                allocations_attributes: [
                  {
                    __id: row.allocation.id,
                    id: row.allocationId,
                    _destroy: true
                  },
                ],
              }
            }

            // Create or update record if either
            // non vCPL with cplInCents or originalCap present OR
            // vCPL with minimumLeadTarget or budgetInCents present
            if((row.payout !== "variable_cost_per_lead" && row.cplInCents !== null || row.originalCap !== null) ||
               (row.payout === "variable_cost_per_lead" && row.minimumLeadTarget !== null || row.budgetInCents !== null)) {
              return {
                __id: row.clientCampaign.id,
                __record: row.clientCampaign,
                id: row.clientCampaignId,
                allocations_attributes: [
                  serializeNestedRecord(store, row.allocation),
                ],
              }
            }
          }).filter(Boolean)
          return newValues
        })

        reloadContent()
      })
      .then(() => {
        onClose()
        addNotification({variant: 'notice', message: 'Allocations import successful. Please review and save your changes.'})
      })
      .catch(error => {
        notifyError(error, { message: `Failed while processing imported allocations` });
        setError(error);
      })
      .finally(() => {
        setProcessing(false);
      });
    }
  };

  const handleError = error => {
    setError(error);
    notifyError(error, { message: `Failed to import allocations` });
  };

  return (
    <StandardDialog
      title='Import Allocations'
      onCancel={onClose}
      onClose={onClose}
      onConfirm={handleFile}
      disabled={!file}
      confirmText='Import'
      maxWidth='600px'
      description={<>Select the <strong>CSV file</strong> to import for the allocations table. Only columns and inputs currently in view will be affected by import.</>}
    >
      <FilePickerWithLabel
        file={file}
        setFile={setFile}
        fileDescriptor='CSV'
        extensions={['csv']}
        onChange={(file) => { setFile(file) }}
        onError={handleError}
        isSubmitting={processing}
      />
      {error && <Typography sx={{color: 'error.main'}}>{error.toString()}</Typography>}
    </StandardDialog>
  )
}

function ExportAllocationDialog({onClose}) {
  const dataManager = useDataManager()

  const handleExport = useCallback(() => {
    onClose()
    downloadData(dataManager.getCsvAllocationData(), 'allocations.csv', 'text/csv')
  }, [dataManager, onClose])
  return (
    <StandardDialog
      title={`Export Allocations`}
      onCancel={onClose}
      onConfirm={() => handleExport()}
      confirmText='Export'
      onClose={onClose}
      maxWidth='600px'
      description={<>Only columns and vendors <strong>currently in view</strong> will be included in download.</>}
    >
    </StandardDialog>
  )
}

function AllocationTableTitle({month, setMonth, maxMonth, clientFromRoute, contractFromRoute, contract, setContract, clientId, setDailyCapsOpen, setExportOpen, setImportOpen, permitEditCap, onAddCampaign}) {
  const { getClientRole } = useCurrentUser()
  const hasPermitEditRole = !!getIn(getClientRole(clientId), 'attributes.permitEdit')
  const permitAddCampaigns = !isAllContract(contract) && hasPermitEditRole

  const tableActions = useMemo(() => {
    const showDailyCap = contract && !isAllContract(contract)

    return [
      permitAddCampaigns && {
        icon: () => <AddCircleIcon/>,
        tooltip: 'Add campaign',
        onClick: () => onAddCampaign(),
      },
      showDailyCap && {
        icon: () => <DateRangeIcon/>,
        tooltip: 'Edit daily caps',
        onClick: () => setDailyCapsOpen(true),
      },
      {
        icon: () => <DownloadImage/>,
        tooltip: 'Export to CSV',
        onClick: () => setExportOpen(true),
      },
      permitEditCap && {
        icon: () => <UploadIcon/>,
        tooltip: 'Import CSV',
        onClick: () => setImportOpen(true),
      },
    ].filter(Boolean)
  }, [contract, onAddCampaign, permitAddCampaigns, permitEditCap, setDailyCapsOpen, setExportOpen, setImportOpen])

  return(
    <>
      <TableTitle title='Campaign Allocations' actions={tableActions} columnsButton={true}>
        <MonthSelector month={month} setMonth={setMonth} maxMonth={maxMonth}/>
        {clientFromRoute && !contractFromRoute && (
          <ContractSelector
            contract={contract}
            setContract={setContract}
            model="vendor"
            clientId={clientId}
            showAll={true}
          />
        )}
      </TableTitle>
    </>
  )
}

function AllocationsTableContainer({contractFromRoute, clientFromRoute, contract, setContract, refreshCampaigns, hasTabNavigation, onEditCampaign, onAddCampaign}) {
  const { getRemoteId, isNew, remote, store } = useOrbit()
  const { accessibleVendorContracts, getClientRole } = useCurrentUser()
  const { clientId } = useParams()
  const { isFeatureFlagEnabled } = useFeatures()

  const hasPermitEditRole = !!getIn(getClientRole(clientId), 'attributes.permitEdit')
  const permitDeleteAllocation = hasPermitEditRole
  const permitEditDailyCaps = hasPermitEditRole
  const permitEditCampaigns = hasPermitEditRole
  const permitEditCap = hasPermitEditRole
  const permitEditCPL = hasPermitEditRole
  const permitEditLeadTarget = hasPermitEditRole
  const permitEditSpend = hasPermitEditRole
  const permitEditBudget = hasPermitEditRole
  const permitEditVendorProjection = !isFeatureFlagEnabled('disable_edit_vendor_projections') && accessibleVendorContracts.some(c => c.id === contractFromRoute?.id)
  const permitSave = permitEditCap || permitEditCPL || permitEditVendorProjection

  // Global admin is intentionally excluded from sending IO
  const permitSendIO = hasPermitEditRole

  const client = clientFromRoute || store.cache.query(q => q.findRelatedRecord(contractFromRoute, 'client'))
  const timeZone = useMemo(() => (client && getIn(client, 'attributes.timeZone')), [client])
  const [month, setMonth] = useState(() => moment.tz(timeZone))
  const currentMonth = moment.tz(timeZone).utc(true).startOf('month')
  const maxMonth = currentMonth.clone().add(1, 'month')
  const [dailyCapsOpen, setDailyCapsOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [currentAllocation, setCurrentAllocation] = useState({open: false, allocation: {}})
  const [data, setData] = useState<AllocationRow[] | null>(null)
  const [promise, setPromise] = useState<Promise<AllocationRow[]> | null>(null)
  const [refreshAllocations, setRefreshAllocations] = useState(false)

  const isSmartScoreEnabled = isFeatureFlagEnabled('smart_score')

  const contractOrClient = isAllContract(contract) ? client : contract
  const allocationsResource = useMemo(() => { void(refreshAllocations); return contractOrClient && new AllocationsResource({remote, contractOrClient: contractOrClient, year: month.year(), month: month.month()+1}) }, [remote, contractOrClient, month, refreshAllocations])
  const clientCampaignsResource = useMemo(() => {void(refreshCampaigns); return contractOrClient && new ClientCampaignsResource({remote, contractOrClient: contractOrClient, year: month.year(), month: month.month()+1, programGroups: true})}, [contractOrClient, month, refreshCampaigns, remote]) // TODO: this is hacky, using "refreshCampaigns" to force reloading of the resource after creating a new campaign

  const contractsResource = useMemo(() => new ContractsResource({remote, clientId}), [clientId, remote])

  const campaignCaps = useMemo(() => (
    data ? data.map(row => ({clientCampaign: row.clientCampaign, cap: row.adjustedCap ?? row.originalCap ?? 0})) : []
  ), [data])

  const newAllocation = useCallback((clientCampaign, year: number, month) => {
    const allocation = { type: 'allocation', attributes: { year, month }, relationships: { clientCampaign: { data: clientCampaign } } }
    store.update(q => q.addRecord(allocation))
    return allocation
  }, [store])

  const getFieldMetadata = useCallback(({payout, allocation}) => {
    return {
      cplInCents: {
        editable: permitEditCPL,
        disabled: isVariableCpl(payout),
      },
      originalCap: {
        editable: permitEditCap,
        disabled: isVariableCpl(payout),
      },
      adjustedCap: {
        editable: permitEditCap,
        disabled: isVariableCpl(payout),
      },
      minimumLeadTarget: {
        editable: isVariableCpl(payout) && permitEditLeadTarget,
      },
      totalCostInCents: {
        editable: isVariableCpl(payout) && permitEditSpend,
        disabled: !getRemoteId(allocation),
      },
      budgetInCents: {
        editable: isVariableCpl(payout) && permitEditBudget,
      },
      vendorProjection: {
        editable: permitEditVendorProjection,
      },
    }
  }, [getRemoteId, permitEditBudget, permitEditCPL, permitEditCap, permitEditLeadTarget, permitEditSpend, permitEditVendorProjection])

  useEffect(() => {
    setData(null)
    setPromise(contract && Promise.all([clientCampaignsResource.promise, allocationsResource.promise, contractsResource.promise]).then(([clientCampaigns, allocations, contracts]) => {
      const data = clientCampaigns
        .filter(clientCampaign => getIn(clientCampaign, 'attributes.status') === 'active')
        .map(clientCampaign => {
          const clientCampaignId = getRemoteId(clientCampaign)
          const existingAllocation = allocations.find(allocation => clientCampaign.id === getIn(allocation, 'relationships.clientCampaign.data.id'))
          const allocation = existingAllocation || newAllocation(clientCampaign, month.year(), month.month()+1)
          const originalCap = getIn(allocation, 'attributes.originalCap')
          const adjustedCap = getIn(allocation, 'attributes.adjustedCap')
          const contract = contracts.find(contract => clientCampaign.relationships.contract.data.id === getIn(contract, 'id'))
          const payout = getIn(clientCampaign, 'attributes.payout')
          const vendorName = getIn(store.cache.query(q => q.findRelatedRecord(contract, 'vendor')), 'attributes.name')
          const goodLeadCount = getIn(clientCampaign, 'attributes.goodLeadCount')
          const billableLeadCount = getIn(clientCampaign, 'attributes.billableLeadCount')
          const acceptedLeadCount = payout === 'cost_per_enroll' ? goodLeadCount : billableLeadCount
          // Check if the allocation has the relationship for campaignSpend before finding related records - the constructed newAllocation trips up Orbit
          const campaignSpends = getIn(allocation, 'relationships.campaignSpends') ? store.cache.query(q => q.findRelatedRecords(allocation, 'campaignSpends')) as InitializedRecord[] : undefined
          const totalSpendInCents = sumBy(campaignSpends, cs => (cs.attributes?.spendInCents as number) || 0)

          return {
            id: clientCampaignId,
            clientCampaign: clientCampaign,
            clientCampaignName: clientCampaign.attributes.name,
            clientCampaignId: clientCampaignId,
            clientCampaignPublicId: clientCampaign.attributes.publicId,
            clientCampaignProgramGroup: getIn(store.cache.query(q => q.findRelatedRecord(clientCampaign, 'programGroup')), 'attributes.description'),
            payout: payout,
            smartScore: clientCampaign.attributes.smartScore,
            smartScoreExplanation: clientCampaign.attributes.smartScoreExplanation,
            acceptedLeadCount: acceptedLeadCount,
            totalCostInCents: getIn(clientCampaign, 'attributes.totalCostInCents'),
            cplInCents: getIn(allocation, 'attributes.cplInCents'),
            vendorProjection: getIn(allocation, 'attributes.vendorProjection'),
            originalCap: originalCap,
            adjustedCap: adjustedCap,
            allocationId: getRemoteId(allocation),
            allocation: allocation,
            originalAttributes: { ...allocation.attributes },
            vendorName: vendorName,
            budgetInCents: getIn(allocation, 'attributes.budgetInCents'),
            minimumLeadTarget: getIn(allocation, 'attributes.minimumLeadTarget'),
            campaignSpends: campaignSpends,
            totalSpendInCents: totalSpendInCents,
            fieldMetadata: getFieldMetadata({payout, allocation}),
          }
        })

      setData(data)
      return data
    }))
  }, [contract, clientCampaignsResource, allocationsResource, setData, month, contractsResource.promise, newAllocation, store, getFieldMetadata, getRemoteId])

  const payoutColumnTitle = useMemo(() => getPayoutColumnTitle(data), [data])
  const showBudget = useMemo(() => isBlank(data) || data?.some(row => row.payout !== 'cost_per_enroll'), [data])
  const isVariableCpl = (payout: Payout) => payout === 'variable_cost_per_lead'

  const columns = useMemo(() => [
    { title: 'Campaign',            field: 'clientCampaignName', render: (clientCampaignName) => <Box sx={{ minWidth: 144 }}>{clientCampaignName}</Box>, visible: true, summary: { text: 'Total' } } as const,
    { title: 'Campaign ID',         field: 'clientCampaignPublicId', render: (clientCampaignPublicId) => <Box sx={{ minWidth: 70 }}>{clientCampaignPublicId}</Box>, visible: true } as const,
    { title: 'Program Group',       field: 'clientCampaignProgramGroup', visible: true } as const,
    { title: 'Payout',              field: 'payout', lookup: PAYOUT_LABEL_MAP, visible: true } as const,
    { title: payoutColumnTitle,     field: 'cplInCents', align: 'right', render: (cplInCents, row, { handleChange }) => row.fieldMetadata?.['cplInCents'].editable ? <AllocationField row={row} field="cplInCents" format="currency" onChange={handleChange} disabled={row.fieldMetadata?.['cplInCents'].disabled} /> : toDollars(cplInCents) || '—', visible: true, summary: { text: '' } } as const,
    { title: 'Cap',                 field: 'originalCap', align: 'right', render: (originalCap, row, { handleChange }) => row.fieldMetadata?.['originalCap'].editable ? <AllocationField row={row} field="originalCap" format="number" onChange={handleChange} placeholder="0" disabled={row.fieldMetadata?.['originalCap'].disabled} /> : toFormattedNumber(originalCap), visible: true, summary: data => sum(data, 'originalCap'), renderSummary: originalCap => toFormattedNumber(originalCap) } as const,
    { title: 'Adjusted Cap',        field: 'adjustedCap', align: 'right', render: (adjustedCap, row, { handleChange }) => row.fieldMetadata?.['adjustedCap'].editable ? <AdjustedCapFields row={row} onChange={handleChange} disabled={row.fieldMetadata?.['adjustedCap'].disabled} /> : toFormattedNumber(adjustedCap), visible: true, summary: (data : AllocationRow[]) => sumBy(data, row => row.adjustedCap ?? row.originalCap ?? 0), renderSummary: adjustedCap => toFormattedNumber(adjustedCap) } as const,
    ...(isSmartScoreEnabled && [
      { title: 'Smart Score',       field: 'smartScore', align: 'center', render: (_smartScore, row) => <SmartScore smartScore={row.smartScore} smartScoreExplanation={row.smartScoreExplanation} />, visible: true, summary: { text: '' } } as const,
    ] || []),
    { title: 'Accepted Leads',      field: 'acceptedLeadCount', align: 'right', render: (acceptedLeadCount, row) => { const cap = row.adjustedCap ?? row.originalCap; const capPercent = cap > 0 && row.acceptedLeadCount > 0 ? toFormattedNumber((row.acceptedLeadCount / cap) * 100) : null;  return capPercent ? <Tooltip title={`${capPercent}% Monthly Cap`}><span>{acceptedLeadCount}</span></Tooltip> : acceptedLeadCount }, visible: true, summary: data => sum(data, 'acceptedLeadCount') } as const,
    { title: 'Minimum Lead Target', field: 'minimumLeadTarget', align: 'right', render: (minimumLeadTarget, row, { handleChange }) => row.fieldMetadata?.['minimumLeadTarget'].editable ? <AllocationField row={row} field="minimumLeadTarget" format="number" onChange={handleChange} placeholder="0" /> : isVariableCpl(row.payout) ? toFormattedNumber(minimumLeadTarget) : undefined, visible: true, summary: data => sum(data, 'minimumLeadTarget'), renderSummary: minimumLeadTarget => toFormattedNumber(minimumLeadTarget) } as const,
    { title: 'Current Spend',       field: 'totalCostInCents', render: (currentSpend, row) => row.fieldMetadata?.['totalCostInCents'].editable ? <CurrentSpendField value={toDollars(row.totalSpendInCents, { useGrouping: false, minimumFractionDigits: 2, maximumFractionDigits: 2 })} placeholder="0" onClick={() => setCurrentAllocation({open: true, allocation: row.allocation})} disabled={row.fieldMetadata?.['totalCostInCents'].disabled} /> : isVariableCpl(row.payout) ? toDollars(row.totalSpendInCents) : <Box sx={{minWidth: 79}}>{toDollars(currentSpend)}</Box>, align: 'right', visible: true, summary: data => sum(data, 'totalCostInCents') } as const,
    ...(showBudget && [
      { title: 'Budget',            field: 'budgetInCents', value: row => isVariableCpl(row.payout) ? row.budgetInCents : multiplyOrNull(row.cplInCents, row.adjustedCap ?? row.originalCap), render: (value, row, {handleChange}) => (row.fieldMetadata?.['budgetInCents'].editable ? <AllocationField row={row} field="budgetInCents" format="currency" onChange={handleChange} /> : toDollars(value)), align: 'right', visible: true, summary: (data: AllocationRow[]) => sumBy(data, row =>  row.budgetInCents ?? multiplyOrNull(row.cplInCents, row.adjustedCap ?? row.originalCap) ?? 0) } as const,
    ] || []),
    { title: 'Vendor Projection',   field: 'vendorProjection', align: 'right', render: (vendorProjection, row, { handleChange }) => row.fieldMetadata?.['vendorProjection'].editable ? (!isNew(row.allocation) ? <AllocationField row={row} field="vendorProjection" format="number" onChange={handleChange} /> : null) : toFormattedNumber(vendorProjection), visible: true, summary: data => sum(data, 'vendorProjection'), renderSummary: vendorProjection => toFormattedNumber(vendorProjection) } as const,
  ], [payoutColumnTitle, isSmartScoreEnabled, showBudget, isNew])

  return (
    <TableContainer
      columns={columns}
      data={promise || []}
      hasTabNavigation={hasTabNavigation}
      options={{
        columnsButton: true,
        sortColumn: 'budget',
        sortOrder: 'desc'
      }}
      sx={{
        '& .MuiTableCell-root': {
          height: 60,
        },
        height: '100%',
        pb: !hasTabNavigation ? '30px' : '6px',
      }}
    >
      <AllocationTableTitle
        month={month}
        setMonth={setMonth}
        maxMonth={maxMonth}
        clientFromRoute={clientFromRoute}
        contractFromRoute={contractFromRoute}
        contract={contract}
        setContract={setContract}
        clientId={clientId}
        setDailyCapsOpen={setDailyCapsOpen}
        setExportOpen={setExportOpen}
        setImportOpen={setImportOpen}
        permitEditCap={permitEditCap}
        onAddCampaign={onAddCampaign}
      />

      {client ? (
        <AllocationsTableForm
          month={month}
          data={data}
          setData={setData}
          contract={contract}
          refreshData={refreshAllocations}
          setRefreshData={setRefreshAllocations}
          cplLabel={payoutColumnTitle}
          client={client}
          permitSendIO={permitSendIO}
          permitSave={permitSave}
          importOpen={importOpen}
          setImportOpen={setImportOpen}
          columns={columns}
          permitDeleteAllocation={permitDeleteAllocation}
          permitEditCap={permitEditCap}
          permitEditCPL={permitEditCPL}
          permitEditBudget={permitEditBudget}
          permitEditLeadTarget={permitEditLeadTarget}
          permitEditVendorProjection={permitEditVendorProjection}
          permitEditCampaigns={permitEditCampaigns}
          onEditCampaign={onEditCampaign}
        />
      ) : (
        <Box sx={{
            width: '100%',
            height: '100px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#888'
          }}
        >
          Please select a Vendor
        </Box>
      )}

      {dailyCapsOpen && (
        <DailyCapModal
          contract={contract}
          clientCampaignsResource={clientCampaignsResource}
          onClose={() => setDailyCapsOpen(false)}
          campaignCaps={campaignCaps}
          month={month}
          permitEdit={permitEditDailyCaps}
        />
      )}

      {exportOpen && (
        <ExportAllocationDialog onClose={() => setExportOpen(false)} />
      )}

      {currentAllocation.open && (
        <CampaignSpend
          onClose={() => setCurrentAllocation({ open: false, allocation: {} })}
          allocation={currentAllocation.allocation}
          refreshAllocations={() => setRefreshAllocations(!refreshAllocations)}
          clientTimezone={getIn(client, 'attributes.timeZone')}
        />
      )}
    </TableContainer>
  )
}

export type AddClientCampaignModalState = { open: false } | { open: true, clientCampaign: ClientCampaignRecord }

export default function AllocationsTable({hasTabNavigation=false}) {
  const [modalState, setModalState] = useState<AddClientCampaignModalState>({ open: false })
  const { store } = useOrbit()
  const { clientId, contractId } = useParams()
  const contractFromRoute = contractId ? store.cache.query<ContractRecord>(q => q.findRecord(getRecordIdentity('contract', contractId))) : null
  const clientFromRoute = clientId ? store.cache.query(q => q.findRecord(getRecordIdentity('client', clientId))) as InitializedRecord : null

  const [localContract, setLocalContract] = useState<ContractRecord>()
  const [refreshCampaigns, setRefreshCampaigns] = useState(false)
  const contract = contractFromRoute || localContract

  // Hacky way of resetting contract the currently set one not present in ContractSelector options
  // TODO: a better solution here once the ideal scope behavior is figured out
  useEffect(() => {
    setLocalContract(contractFromRoute || undefined)
  }, [clientId, contractId, contractFromRoute])

  const handleClose = useCallback(() => {
    setModalState({ open: false })
  }, [])

  const handleSave = useCallback(() => {
    setModalState({ open: false })
    setRefreshCampaigns(refreshCampaigns => !refreshCampaigns)
  }, [])

  const handleEditCampaign = useCallback((campaignId: string) => {
    setModalState({ open: true, clientCampaign: store.cache.query<ClientCampaignRecord>(q => q.findRecord(getRecordIdentity('clientCampaign', String(campaignId)))) })
  }, [store])

  return (
    <Box sx={{ minHeight: 0 }}>
      <AllocationsTableContainer
        contractFromRoute={contractFromRoute}
        clientFromRoute={clientFromRoute}
        contract={contract}
        setContract={setLocalContract}
        refreshCampaigns={refreshCampaigns}
        hasTabNavigation={hasTabNavigation}
        onEditCampaign={handleEditCampaign}
        onAddCampaign={() => setModalState({ open: true, clientCampaign: newRecord<ClientCampaignRecord>({ type: 'clientCampaign' }) })}
      />
      {modalState.open && (
        <AddClientCampaignModal
          clientCampaign={modalState.clientCampaign}
          clientId={clientId}
          contract={contract}
          onClose={handleClose}
          onSave={handleSave}
        />
      )}
    </Box>
  )
}
